/* Space Armory cart operations. A successful mutation is never retried to repair a view. */
(function (scope) {
  'use strict';
  const SWAP = '_SpaceArmoryVariantSwap';
  const COVER = '_SpaceArmoryCheckoutPlus';
  const isCoverage = (item, config) => String(item.product_id) === String(config.protection?.id);
  const merchandise = (cart, config) => cart.items.filter(item => !isCoverage(item, config));
  function protectionVariant(cart, config) {
    const items = merchandise(cart, config);
    if (!items.some(item => item.requires_shipping !== false) || !config.protection?.variants?.length) return null;
    const rate = Number(config.rate);
    if (!(rate > 0)) return null;
    const subtotal = items.reduce((sum, item) => sum + item.final_line_price, 0) / rate;
    const index = subtotal < 9999 ? 0 : subtotal < 15000 ? 1 : 2;
    const variant = config.protection.variants[index];
    return variant?.available ? variant : null;
  }
  function checkoutReady(cart, config) {
    if (!cart || !merchandise(cart, config).length || cart.items.some(item => item.properties?.[SWAP])) return false;
    const coverage = cart.items.filter(item => isCoverage(item, config));
    if (!coverage.length) return true;
    const target = protectionVariant(cart, config);
    return coverage.length === 1 && coverage[0].quantity === 1 && String(coverage[0].variant_id) === String(target?.id);
  }
  class CartStore {
    constructor({request, config, onChange = () => {}, onBusy = () => {}, onError = () => {}}) {
      Object.assign(this, {request, config, onChange, onBusy, onError});
      this.cart = null;
      this.tail = Promise.resolve();
      this.pending = 0;
      this.mutating = 0;
      this.confirmedAt = 0;
      this.needsRead = false;
    }
    async read() {
      const cart = await this.request('cart.js');
      if (!cart || !Array.isArray(cart.items) || !cart.currency) throw new Error('Your cart could not be loaded. Please try again.');
      this.cart = cart;
      this.confirmedAt = Date.now();
      this.needsRead = false;
      this.unconfirmed = false;
      return cart;
    }
    async post(route, body) { this.needsRead = true; return this.request(route, body); }
    async change(key, quantity, properties) {
      const payload = {id: key, quantity};
      if (properties !== undefined) payload.properties = properties;
      this.cart = await this.post('cart/change.js', payload);
      this.confirmedAt = Date.now();
      this.needsRead = false;
      return this.cart;
    }
    async recoverSwaps() {
      for (const entry of [...this.cart.items]) {
        if (!entry.properties?.[SWAP]) continue;
        let journal;
        try { journal = JSON.parse(entry.properties[SWAP]); } catch { continue; }
        if (!journal.originalKey || journal.version !== 1) continue;
        const original = this.cart.items.find(item => item.key === journal.originalKey);
        if (original) await this.change(entry.key, 0);
        else {
          const properties = {...entry.properties};
          delete properties[SWAP];
          await this.change(entry.key, entry.quantity, properties);
        }
      }
    }
    async reconcileCoverage(choice) {
      const existing = this.cart.items.filter(item => isCoverage(item, this.config));
      const selected = choice === undefined ? existing.length > 0 : choice;
      const target = selected ? protectionVariant(this.cart, this.config) : null;
      let kept = false;
      for (const item of existing) {
        if (target && String(item.variant_id) === String(target.id) && !kept) {
          kept = true;
          if (item.quantity !== 1) await this.change(item.key, 1);
        } else await this.change(item.key, 0);
      }
      if (target && !kept) {
        await this.post('cart/add.js', {items: [{id: target.id, quantity: 1, properties: {[COVER]: 'Order protection'}}]});
        await this.read();
      }
      if (choice === true && !target) throw new Error('Checkout+ is unavailable for this cart. You can continue without it.');
    }
    run(operation, {repair = true, forceRead = false, readonly = false} = {}) {
      this.pending += 1;
      if (!readonly) this.mutating += 1;
      this.onBusy(true);
      const work = this.tail.catch(() => {}).then(async () => {
        try {
          if (forceRead || !this.cart || this.unconfirmed || Date.now() - this.confirmedAt > 15000) await this.read();
          await this.recoverSwaps();
          const result = await operation();
          // The requested write has succeeded. A later refresh failure must not make it look safe to repeat it.
          try {
            if (this.needsRead) await this.read();
            if (repair) await this.reconcileCoverage();
          } catch (error) {
            this.onError('Your change was saved, but the cart could not be refreshed. Reopen the cart before continuing.');
            this.unconfirmed = true;
          }
          this.onChange(this.cart);
          return result;
        } catch (error) {
          try {
            await this.read();
            await this.recoverSwaps();
            this.onChange(this.cart);
          } catch { this.unconfirmed = true; }
          this.onError(error.message || 'We could not confirm this change. Please review your cart before trying again.');
          throw error;
        }
      }).finally(() => {
        this.pending -= 1;
        if (!readonly) this.mutating -= 1;
        this.onBusy(this.pending > 0);
      });
      this.tail = work;
      return work;
    }
    refresh({maxAge = 0} = {}) {
      if (!this.pending && !this.unconfirmed && this.cart && Date.now() - this.confirmedAt < maxAge &&
          (checkoutReady(this.cart, this.config) || this.cart.items.length === 0)) return Promise.resolve(this.cart);
      return this.run(async () => {
        await this.reconcileCoverage();
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true, readonly: true});
    }
    addForm(formData, {onAdded} = {}) {
      scope.SpaceArmoryFitDevice?.decorateForm(formData);
      formData.delete('sections');
      formData.delete('sections_url');
      return this.run(async () => {
        const response = await this.post('cart/add.js', formData);
        try { onAdded?.(); } catch (error) { console.error(error); }
        return response;
      });
    }
    add(id, quantity = 1, properties = {}) {
      properties = {...properties, ...scope.SpaceArmoryFitDevice?.cartProperties()};
      return this.run(() => this.post('cart/add.js', {items: [{id, quantity, properties}]}));
    }
    setQuantity(key, quantity) {
      if (!Number.isInteger(quantity) || quantity < 0) return Promise.reject(new Error('Enter a valid quantity.'));
      return this.run(async () => {
        if (!this.cart.items.some(item => item.key === key)) throw new Error('This item changed. Please review your cart.');
        return this.change(key, quantity);
      });
    }
    swapVariant(key, id, propertyPatch) {
      return this.run(async () => {
        const original = this.cart.items.find(item => item.key === key);
        if (!original) throw new Error('This item changed. Please review your cart.');
        const merged = {...original.properties, ...propertyPatch};
        Object.keys(merged).forEach(name => { if (merged[name] === null) delete merged[name]; });
        if (String(original.variant_id) === String(id)) return propertyPatch ? this.change(key, original.quantity, merged) : this.cart;
        const properties = {...merged, [SWAP]: JSON.stringify({version: 1, originalKey: key})};
        const item = {id, quantity: original.quantity, properties};
        const sellingPlan = original.selling_plan_allocation?.selling_plan?.id;
        if (sellingPlan) item.selling_plan = sellingPlan;
        // An isolated new line makes an interrupted swap recoverable without touching an existing matching line.
        await this.post('cart/add.js', {items: [item]});
        await this.read();
        const replacement = this.cart.items.find(line => line.properties?.[SWAP] === properties[SWAP]);
        if (!replacement || replacement.quantity !== original.quantity) throw new Error('That quantity is unavailable in this size. Your original item has been kept.');
        await this.change(key, 0);
        await this.recoverSwaps();
        return this.cart;
      }, {forceRead: true});
    }
    setProtection(selected) {
      return this.run(async () => {
        await this.reconcileCoverage(selected);
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true});
    }
    checkout(includeProtection) {
      return this.run(async () => {
        if (!merchandise(this.cart, this.config).length) throw new Error('Your cart is empty.');
        await this.reconcileCoverage(includeProtection);
        this.unconfirmed = false;
        return this.cart;
      }, {repair: false, forceRead: true});
    }
  }
  scope.SpaceArmoryCartCore = {CartStore, protectionVariant, checkoutReady, merchandise, isCoverage, SWAP, COVER};
})(typeof window === 'undefined' ? globalThis : window);
